import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    MenuItem,
    Alert,
} from '@mui/material';
import api from '../../utils/api';

const CategoryDialog = ({ open, category, categories = [], onClose, parent }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        parentId: ''
    });
    
    useEffect(() => {
        if (open) {
            if (category) {
                setFormData({
                    name: category.name || '',
                    description: category.description || '',
                    parentId: category.parent_id || ''
                });
            } else if (parent) {
                setFormData({
                    name: '',
                    description: '',
                    parentId: parent.id
                });
            } else {
                setFormData({
                    name: '',
                    description: '',
                    parentId: ''
                });
            }
        }
    }, [open, category, parent]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            // Convert empty parentId to null
            const dataToSend = {
                ...formData,
                parentId: formData.parentId || null
            };
            
            let successMessage;
            if (category) {
                await api.put(`/products/categories/${category.id}`, dataToSend);
                successMessage = `Category "${formData.name}" updated successfully!`;
            } else {
                await api.post('/products/categories', dataToSend);
                successMessage = `Category "${formData.name}" created successfully!`;
            }
            
            onClose(successMessage);
        } catch (error) {
            console.error('Error saving category:', error);
            setError(error.response?.data?.message || 'Error saving category');
        } finally {
            setLoading(false);
        }
    };
    
    // Filter out the current category and its descendants from parent options
    const getValidParentOptions = () => {
        if (!Array.isArray(categories) || categories.length === 0) return [];
        if (!category) return categories;
        
        const isDescendant = (parent, child) => {
            if (!parent || !child) return false;
            if (parent.id === child.id) return true;
            return parent.path_ids && parent.path_ids.includes(child.id);
        };
        
        return categories.filter(c => !isDescendant(c, category));
    };
    
    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>
                {category ? 'Edit Category' : 'Add New Category'}
            </DialogTitle>
            
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                required
                                label="Category Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={12}>
                            <TextField
                                fullWidth
                                select
                                label="Parent Category"
                                name="parentId"
                                value={formData.parentId}
                                onChange={handleChange}
                            >
                                <MenuItem value="">No Parent (Root Category)</MenuItem>
                                {getValidParentOptions().map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.path || cat.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                
                <DialogActions>
                    <Button onClick={() => onClose()}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : (category ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CategoryDialog; 